import { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  TextField,
  Chip,
  CircularProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Papa from 'papaparse';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { useBulkImportTasksMutation } from '../redux/firestoreApi';
import { showToast } from '../redux/uiSlice';
import PageHeader from '../components/common/PageHeader';
import {
  STANDARD_TARGETS,
  StandardField,
  autoDetectMapping,
  normalisePriority,
  normaliseStatus,
} from '../utils/csvMapping';

type Mapping = Record<StandardField, string | null>;

const steps = ['Upload CSV', 'Map fields', 'Preview', 'Import'];

export default function CsvImportPage() {
  const uid = useAppSelector((s) => s.session.uid)!;
  const projectId = useAppSelector((s) => s.session.activeProjectId);
  const sprintId = useAppSelector((s) => s.session.activeSprintId);
  const dispatch = useAppDispatch();

  const [step, setStep] = useState(0);
  const [fileName, setFileName] = useState<string>('');
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Mapping>({
    ticketNo: null,
    title: null,
    description: null,
    assignee: null,
    status: null,
    priority: null,
    storyPoints: null,
  });
  const [parsing, setParsing] = useState(false);
  const [bulkImport, { isLoading: importing }] = useBulkImportTasksMutation();

  if (!projectId || !sprintId) {
    return (
      <>
        <PageHeader title="CSV Import" />
        <Alert severity="info">
          Select a project and sprint before importing.
        </Alert>
      </>
    );
  }

  const onFileChange = (file: File) => {
    setFileName(file.name);
    setParsing(true);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const cols = res.meta.fields ?? [];
        setColumns(cols);
        setRows(res.data);
        setMapping(autoDetectMapping(cols));
        setParsing(false);
        setStep(1);
      },
      error: (err) => {
        setParsing(false);
        dispatch(showToast({ message: `Parse error: ${err.message}`, severity: 'error' }));
      },
    });
  };

  const ready = mapping.ticketNo && mapping.title;

  const previewRows = useMemo(() => {
    return rows.slice(0, 10).map((r) => buildTask(r, mapping));
  }, [rows, mapping]);

  const doImport = async () => {
    const tasks = rows.map((r) => buildTask(r, mapping));
    try {
      const n = await bulkImport({ uid, projectId, sprintId, tasks }).unwrap();
      dispatch(showToast({ message: `Imported ${n} tasks`, severity: 'success' }));
      setStep(3);
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Import failed', severity: 'error' }));
    }
  };

  const reset = () => {
    setStep(0);
    setFileName('');
    setRows([]);
    setColumns([]);
    setMapping({
      ticketNo: null,
      title: null,
      description: null,
      assignee: null,
      status: null,
      priority: null,
      storyPoints: null,
    });
  };

  return (
    <>
      <PageHeader
        title="CSV Import"
        subtitle="Import tasks exported from Jira into the active sprint."
      />

      <Paper sx={{ p: 3, mb: 2 }} variant="outlined">
        <Stepper activeStep={step} alternativeLabel>
          {steps.map((s) => (
            <Step key={s}>
              <StepLabel>{s}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {step === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center' }} variant="outlined">
          <UploadFileIcon sx={{ fontSize: 56, opacity: 0.6 }} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Select a CSV file
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Common Jira columns (Issue Key, Summary, Status…) are detected automatically.
          </Typography>
          <Button variant="contained" component="label" disabled={parsing}>
            {parsing ? 'Parsing…' : 'Choose CSV'}
            <input
              hidden
              accept=".csv,text/csv"
              type="file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onFileChange(f);
              }}
            />
          </Button>
        </Paper>
      )}

      {step === 1 && (
        <Paper sx={{ p: 3 }} variant="outlined">
          <Typography variant="subtitle1" gutterBottom>
            Map columns — file:{' '}
            <Chip size="small" label={fileName} sx={{ ml: 1 }} />
            <Chip
              size="small"
              label={`${rows.length} rows`}
              sx={{ ml: 1 }}
              color="primary"
              variant="outlined"
            />
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
            Match each column to a system field. Ticket No. and Title are required.
          </Typography>

          <Stack spacing={2}>
            {STANDARD_TARGETS.map((t) => (
              <Stack
                key={t.key}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ sm: 'center' }}
              >
                <Box sx={{ minWidth: 180 }}>
                  <Typography>
                    {t.label}
                    {t.required && (
                      <Chip
                        size="small"
                        label="required"
                        color="warning"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    e.g. {t.examples.join(', ')}
                  </Typography>
                </Box>
                <TextField
                  select
                  value={mapping[t.key] ?? ''}
                  onChange={(e) =>
                    setMapping({ ...mapping, [t.key]: e.target.value || null })
                  }
                  sx={{ flexGrow: 1 }}
                >
                  <MenuItem value="">
                    <em>— ignore —</em>
                  </MenuItem>
                  {columns.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            ))}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: 3 }} justifyContent="flex-end">
            <Button onClick={reset}>Cancel</Button>
            <Button variant="contained" disabled={!ready} onClick={() => setStep(2)}>
              Preview
            </Button>
          </Stack>
        </Paper>
      )}

      {step === 2 && (
        <Paper sx={{ p: 3 }} variant="outlined">
          <Typography variant="subtitle1" gutterBottom>
            Preview (first 10 of {rows.length})
          </Typography>
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Ticket</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Assignee</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell align="right">SP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.ticketNo}</TableCell>
                    <TableCell>{r.title}</TableCell>
                    <TableCell>{r.assignee}</TableCell>
                    <TableCell>{r.status}</TableCell>
                    <TableCell>{r.priority}</TableCell>
                    <TableCell align="right">{r.storyPoints ?? ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" spacing={1} sx={{ mt: 3 }} justifyContent="flex-end">
            <Button onClick={() => setStep(1)}>Back</Button>
            <Button
              variant="contained"
              onClick={doImport}
              disabled={importing}
              startIcon={importing ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Import {rows.length} tasks
            </Button>
          </Stack>
        </Paper>
      )}

      {step === 3 && (
        <Paper sx={{ p: 6, textAlign: 'center' }} variant="outlined">
          <Typography variant="h5">Import complete</Typography>
          <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            {rows.length} tasks were added to the active sprint.
          </Typography>
          <Button variant="contained" onClick={reset}>
            Import another file
          </Button>
        </Paper>
      )}
    </>
  );
}

function buildTask(row: Record<string, string>, mapping: Mapping) {
  const get = (k: StandardField) => (mapping[k] ? row[mapping[k]!] ?? '' : '');
  const sp = get('storyPoints');
  return {
    ticketNo: get('ticketNo').trim(),
    title: get('title').trim() || '(untitled)',
    description: get('description') || '',
    assignee: get('assignee') || '',
    status: normaliseStatus(get('status')),
    priority: normalisePriority(get('priority')),
    storyPoints: sp ? Number(sp) || 0 : 0,
  };
}
